import { StyleSheet } from "@react-pdf/renderer";
import colors from "core/resources/theme/colors";
import { PDFTableTitleRowVariant } from "./Table/TableTitleRow";

interface StyleSheetProps extends Record<string, any> {
  body?: {
    fontSize?: number;
    lineHeight?: number;
  };
  tableTitleRow?: {
    variant?: PDFTableTitleRowVariant;
  };
  tableTitleRowCell?: {
    width?: string | number;
    flex?: number;
    flexShrink?: number;
    flexGrow?: number;
    textAlign?: "left" | "center" | "right";
    color?: string;
    fontStyle?: string;
    fontSize?: number;
  };
  tableRow?: {
    backgroundColor?: string;
  };
  tableRowCell?: {
    width?: string | number;
    flex?: number;
    flexShrink?: number;
    flexGrow?: number;
    textAlign?: "left" | "center" | "right";
    fontSize?: number;
  };
  headerCard?: {
    firstItem?: boolean;
    lastItem?: boolean;
    alignItems?: "flex-start" | "center" | "flex-end";
  };
  paddings?: {
    header: string;
    body: string;
    footer: string;
  };
}

export const makePdfStyleSheet = (props?: StyleSheetProps) =>
  StyleSheet.create({
    page: {
      fontFamily: "Poppins",
      fontStyle: "normal",
      fontSize: 8.5,
      paddingBottom: "30px",
      paddingTop: "0.8cm",
    },
    body: {
      padding: props?.paddings?.body ?? "0.5cm 1.2cm 100px 1.2cm",
      backgroundColor: "white",
      ...props?.body,
    },
    header: {
      backgroundColor: colors.blue150,
      height: "90px",
      padding: props?.paddings?.header ?? "0.5cm 1.2cm",
      flexGrow: 0,
      flexShrink: 0,
      marginTop: "-0.8cm",
    },
    footer: {
      height: "90px",
      padding: props?.paddings?.footer ?? "0.5cm 1.2cm",
      borderTopColor: colors.black100,
      borderTopWidth: 2,
      marginTop: "auto",
      flexGrow: 0,
      flexShrink: 0,
      justifyContent: "center",
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
    },
    logo: {
      width: "40px",
      height: "60px",
      objectFit: "contain",
    },
    topRightLogo: {
      width: "auto",
      height: "24px",
      objectFit: "contain",
      marginLeft: "auto",
      alignSelf: "center",
    },
    logoFooter: {
      width: "auto",
      height: "24px",
      objectFit: "contain",
      alignSelf: "center",
    },
    cover: {
      backgroundColor: colors.blue150,
      height: "100%",
      width: "100%",
      padding: "2cm",
      flexGrow: 1,
    },
    logoCover: {
      width: "100%",
      height: "200px",
      objectFit: "contain",
      marginVertical: "auto",
    },
    title: {
      fontWeight: "medium",
      fontSize: 12,
    },
    tenantNome: {
      fontSize: 11,
    },
    coverTitle: {
      fontWeight: "medium",
      fontSize: 20,
      textAlign: "center",
      marginBottom: "12px",
    },
    coverTenant: {
      color: colors.darkGray,
      fontSize: 14,
      textAlign: "center",
    },
    coverDate: {
      color: colors.darkGray,
      fontSize: 14,
      textAlign: "center",
    },
    subTitle: {
      fontSize: 10,
      marginTop: 4,
    },
    footerTitle: {
      fontWeight: "medium",
      fontSize: 11,
      marginBottom: 4,
    },
    headerDescription: {
      marginLeft: "12px",
      flex: 1,
      height: "60px",
      alignSelf: "center",
    },
    headerCardsContainer: {
      marginLeft: "auto",
      flex: 0.75,
      alignSelf: "center",
      height: "32px",
      flexDirection: "row",
      backgroundColor: "transparent",
      justifyContent: "flex-end",
      fontSize: 9,
      paddingLeft: "8px",
    },
    headerCard: {
      borderTopLeftRadius:
        props?.headerCard?.firstItem && !props?.headerCard?.lastItem
          ? "16px"
          : "4px",
      borderBottomLeftRadius: "4px",
      borderTopRightRadius: "4px",
      borderBottomRightRadius:
        props?.headerCard?.lastItem && !props?.headerCard?.firstItem
          ? "16px"
          : "4px",
      paddingHorizontal: "10px",
      paddingVertical: "2px",
      backgroundColor: "white",
      marginRight: props?.headerCard?.lastItem ? "0px" : "8px",
      justifyContent: "center",
      ...props?.headerCard,
    },
    headerCardValue: {
      fontWeight: "bold",
    },
    footerDescription: {
      marginRight: "auto",
      flex: 1,
      height: "60px",
      alignSelf: "center",
      marginTop: "8px",
    },
    flexRow: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    tableTitleRow: {
      color:
        props?.tableTitleRow?.variant === "primary" ? colors.black : undefined,
      borderBottomWidth: props?.tableTitleRow?.variant === "primary" ? 2 : 0,
      borderBottomColor: colors.black500,
      backgroundColor:
        props?.tableTitleRow?.variant === "primary" ? "white" : colors.black100,
      fontWeight: "medium",
      paddingVertical:
        props?.tableTitleRow?.variant === "primary" ? undefined : 2,
      fontSize: props?.tableTitleRow?.variant === "primary" ? 8 : undefined,
    },
    tableTitleRowCell: {
      flex: 1,
      width: "100%",
      flexGrow: 1,
      flexShrink: 1,
      paddingRight: 4,
      ...props?.tableTitleRowCell,
    },
    tableRow: {
      paddingVertical: 2,
      ...props?.tableRow,
    },
    tableRowCell: {
      flex: 1,
      width: "100%",
      flexGrow: 1,
      flexShrink: 1,
      paddingRight: 4,
      fontSize: props?.tableTitleRow?.variant === "primary" ? 8 : undefined,
      ...props?.tableRowCell,
    },
    horizontalTable: {
      borderWidth: 1,
      borderColor: colors.gray50,
      paddingHorizontal: 4,
      paddingVertical: 2,
      width: "100%",
    },
    horizontalTableTitle: {
      fontWeight: "bold",
      fontSize: 9,
      marginBottom: 2,
    },
    horizontalTableRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      marginBottom: 2,
    },
    horizontalTableTotalRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      borderTopWidth: 1,
      borderTopColor: colors.gray50,
      marginTop: 4,
      fontWeight: "bold",
      paddingVertical: 1,
    },
    horizontalTableValue: {
      fontWeight: "medium",
    },
    horizontalTableTotalRowTitle: {
      fontWeight: "bold",
    },
  });
